
// ---------------------------------------------------------------------------------------------------------------
// debug.js — physics debug overlay (toggle with D): collider / flipper / ball wireframes projected onto the 3D view
// plus a top-down minimap of the whole table drawn on a fixed 2D canvas over the WebGL canvas.
//
// The overlay canvas is created on the first toggle and lives in the DOM only while the overlay is enabled, so the
// page carries no extra full-screen compositing layer otherwise. Its backing store is sized in device pixels
// (devicePixelRatio) and drawn in CSS pixels through a context transform, so lines stay crisp on HiDPI screens.
// It sits at z-index 9: above the vignette (z 8) and below every piece of page chrome (HUD/hint z 10, pause
// overlay 20, toolbar 25, help 30). The minimap is placed inside the free band that the chrome overlapping its
// column leaves - the HUD score box pushes it down, a bottom-anchored toolbar caps its height - measured per draw,
// and capped at 40% of the viewport.
//
// Public names: LAYER_COLORS, DebugOverlay.
// Depends on: THREE.Vector3, TABLE (table.js), Renderer.fw() + Renderer.camera (+ optional Renderer.machineView).
// ---------------------------------------------------------------------------------------------------------------

/** Stroke colour per collision-layer bitmask (1 = playfield, 2/4/8/16 = ramps and raised levels). */
const LAYER_COLORS = { 1: "#7fd0ff", 2: "#ff7fd0", 4: "#a5ff7f", 8: "#ffb26e", 16: "#d9a5ff" };

/** Largest device pixel ratio the overlay renders at (a 4K phone at dpr 4 does not need a 4x debug canvas). */
const DEBUG_MAX_DPR = 3;

class DebugOverlay {
  /**
   * @param {object} table    buildTable() result (unused directly; kept for parity with the bundle)
   * @param {Renderer} renderer provides fw(x, y, z) field->world mapping and the active camera
   */
  constructor(table, renderer) {
    this.T = table;
    this.r3 = renderer;
    this.enabled = false;
    this.cv = null; // created lazily by ensureCanvas() on the first toggle
    this.ctx = null;
    this.w = 0; // canvas size in CSS pixels and the device pixel ratio the backing store was last sized for
    this.h = 0;
    this.dpr = 1;
    this.map = { x: 0, y: 0, w: 0, h: 0 }; // minimap rectangle (CSS px) of the last draw, for tests
    this.v3 = new THREE.Vector3(); // scratch vector for projections
  }

  /** Create the overlay canvas (once). */
  ensureCanvas() {
    if (this.cv) return;
    this.cv = document.createElement("canvas");
    Object.assign(this.cv.style, {
      position: "fixed",
      inset: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: 9,
    });
    this.ctx = this.cv.getContext("2d");
  }

  /** Flip the overlay on/off; the canvas joins the DOM while enabled and is cleared + removed when switched off. */
  toggle() {
    this.enabled = !this.enabled;
    if (this.enabled) {
      this.ensureCanvas();
      if (!this.cv.isConnected) document.body.appendChild(this.cv);
    } else if (this.cv) {
      this.clear();
      this.cv.remove();
    }
    return this.enabled;
  }

  /** Wipe the canvas (no-op before the first toggle). */
  clear() {
    if (!this.ctx) return;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.cv.width, this.cv.height);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  /** Match the backing store to the viewport and device pixel ratio; drawing coordinates stay CSS pixels. */
  fit() {
    const dpr = Math.min(DEBUG_MAX_DPR, Math.max(1, window.devicePixelRatio || 1));
    const w = innerWidth;
    const h = innerHeight;
    if (w === this.w && h === this.h && dpr === this.dpr) return;
    this.w = w;
    this.h = h;
    this.dpr = dpr;
    this.cv.width = Math.round(w * dpr);
    this.cv.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Project a playfield point (x, y, height z) to overlay CSS pixels; null when behind the camera. */
  proj(x, y, z = 0) {
    this.v3.copy(this.r3.fw(x, y, z)).project(this.r3.camera);
    if (this.v3.z > 1) return null;
    return { x: (this.v3.x * 0.5 + 0.5) * this.w, y: (-this.v3.y * 0.5 + 0.5) * this.h };
  }

  /** Stroke a playfield segment at height z (defaults to ball-centre height). */
  line3(x0, y0, x1, y1, z = 0.0135) {
    const from = this.proj(x0, y0, z);
    const to = this.proj(x1, y1, z);
    if (!from || !to) return;
    this.ctx.beginPath();
    this.ctx.moveTo(from.x, from.y);
    this.ctx.lineTo(to.x, to.y);
    this.ctx.stroke();
  }

  /**
   * Vertical band (CSS px) the minimap may use inside the horizontal strip [x0, x1]. The page chrome that overlaps
   * the strip - the HUD score box and the button bar, which stacks under the HUD on wide screens and sits at the
   * bottom edge on phones - is classified by where its middle falls: chrome in the top half pushes {top} down,
   * chrome in the bottom half pulls {bottom} up. Defaults to the whole viewport when neither is there.
   */
  chromeBand(x0, x1) {
    let top = 0;
    let bottom = this.h;
    for (const id of ["hud", "buttons"]) {
      const el = document.getElementById(id);
      if (!el || el.hidden) continue;
      const rect = el.getBoundingClientRect();
      if (!(rect.width > 0 && rect.right > x0 && rect.left < x1)) continue;
      if (rect.top + rect.height / 2 < this.h / 2) top = Math.max(top, rect.bottom);
      else bottom = Math.min(bottom, rect.top);
    }
    return { top, bottom };
  }

  /** Redraw the overlay for the given World (called every rendered frame; returns at once when disabled). */
  draw(world) {
    if (!this.enabled) return; // nothing to clear either: the canvas is out of the DOM while disabled
    this.fit();
    this.clear();
    // The machine viewer shows the closed cabinet; wireframes over it would be meaningless.
    if (this.r3.machineView && this.r3.machineView.active) return;
    const ctx = this.ctx;

    // --- 3D-projected collider wireframes (slightly above the playfield so they are not hidden by it) ---
    ctx.lineCap = "butt";
    ctx.lineWidth = 1.5;
    for (const collider of world.colliders) {
      if (!collider.active) continue;
      ctx.strokeStyle = (LAYER_COLORS[collider.layers] || "#9aa5c2") + "aa";
      if (collider.type === "seg") {
        this.line3(collider.a.x, collider.a.y, collider.b.x, collider.b.y, 0.008);
      } else if (collider.type === "circle") {
        const center = this.proj(collider.c.x, collider.c.y, 0.008);
        const rim = this.proj(collider.c.x + collider.r, collider.c.y, 0.008);
        if (center && rim) {
          ctx.beginPath();
          ctx.arc(center.x, center.y, Math.hypot(rim.x - center.x, rim.y - center.y), 0, 7);
          ctx.stroke();
        }
      } else if (collider.type === "arc") {
        let sweep = (collider.a1 - collider.a0) % (Math.PI * 2);
        if (sweep < 0) sweep += Math.PI * 2;
        let prev = null;
        for (let i = 0; i <= 30; i++) {
          const angle = collider.a0 + (sweep * i) / 30;
          const point = this.proj(
            collider.c.x + Math.cos(angle) * collider.R,
            collider.c.y + Math.sin(angle) * collider.R,
            0.008,
          );
          if (point && prev) {
            ctx.beginPath();
            ctx.moveTo(prev.x, prev.y);
            ctx.lineTo(point.x, point.y);
            ctx.stroke();
          }
          prev = point;
        }
      }
    }

    // --- flippers: pivot->tip line plus the two capsule end circles ---
    ctx.strokeStyle = "#33ff77";
    ctx.lineWidth = 2.5;
    for (const flipper of world.flippers) {
      const tip = flipper.tip();
      this.line3(flipper.pivot.x, flipper.pivot.y, tip.x, tip.y, 0.0135);
      for (const [cx, cy, radius] of [
        [flipper.pivot.x, flipper.pivot.y, flipper.r0],
        [tip.x, tip.y, flipper.r1],
      ]) {
        const center = this.proj(cx, cy, 0.0135);
        const rim = this.proj(cx + radius, cy, 0.0135);
        if (center && rim) {
          ctx.beginPath();
          ctx.arc(center.x, center.y, Math.hypot(rim.x - center.x, rim.y - center.y), 0, 7);
          ctx.stroke();
        }
      }
    }

    // --- balls ---
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    for (const ball of world.balls) {
      if (!ball.active) continue;
      const center = this.proj(ball.p.x, ball.p.y, ball.z + ball.r);
      const rim = this.proj(ball.p.x + ball.r, ball.p.y, ball.z + ball.r);
      if (center && rim) {
        ctx.beginPath();
        ctx.arc(center.x, center.y, Math.hypot(rim.x - center.x, rim.y - center.y), 0, 7);
        ctx.stroke();
      }
    }

    // --- top-down minimap in the free band beside the HUD, playfield metres -> pixels with y up ---
    const aspect = TABLE.W / TABLE.L;
    const mapX = 14;
    let mapW = Math.min(0.4 * this.w, Math.min(0.52 * this.h, 460) * aspect);
    let mapH = mapW / aspect;
    const band = this.chromeBand(mapX - 6, mapX + mapW + 6);
    const mapY = Math.max(64, band.top + 18);
    const floor = Math.min(this.h - 12, band.bottom - 18); // bottom edge, or the top of a bottom-anchored toolbar
    if (mapY + mapH > floor) {
      // Short viewport (or a phone toolbar below): shrink the map so it ends above that edge.
      mapH = Math.max(80, floor - mapY);
      mapW = mapH * aspect;
    }
    this.map = { x: mapX, y: mapY, w: mapW, h: mapH };
    const mx = (x) => mapX + (x / TABLE.W) * mapW;
    const my = (y) => mapY + (1 - y / TABLE.L) * mapH;
    ctx.fillStyle = "rgba(3,4,10,0.82)";
    ctx.fillRect(mapX - 6, mapY - 6, mapW + 12, mapH + 12);
    ctx.lineWidth = 1.2;
    for (const collider of world.colliders) {
      if (!collider.active) continue;
      ctx.strokeStyle = LAYER_COLORS[collider.layers] || "#9aa5c2";
      ctx.globalAlpha = collider.layers === 1 ? 0.95 : 0.55; // raised layers drawn fainter
      if (collider.type === "seg") {
        ctx.beginPath();
        ctx.moveTo(mx(collider.a.x), my(collider.a.y));
        ctx.lineTo(mx(collider.b.x), my(collider.b.y));
        ctx.stroke();
      } else if (collider.type === "circle") {
        ctx.beginPath();
        ctx.arc(mx(collider.c.x), my(collider.c.y), (collider.r / TABLE.W) * mapW, 0, 7);
        ctx.stroke();
      } else if (collider.type === "arc") {
        let sweep = (collider.a1 - collider.a0) % (Math.PI * 2);
        if (sweep < 0) sweep += Math.PI * 2;
        ctx.beginPath();
        for (let i = 0; i <= 32; i++) {
          const angle = collider.a0 + (sweep * i) / 32;
          const px = mx(collider.c.x + Math.cos(angle) * collider.R);
          const py = my(collider.c.y + Math.sin(angle) * collider.R);
          if (i) ctx.lineTo(px, py);
          else ctx.moveTo(px, py);
        }
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = "#33ff77";
    ctx.lineWidth = Math.max(2, (0.019 / TABLE.W) * mapW);
    ctx.lineCap = "round";
    for (const flipper of world.flippers) {
      const tip = flipper.tip();
      ctx.beginPath();
      ctx.moveTo(mx(flipper.pivot.x), my(flipper.pivot.y));
      ctx.lineTo(mx(tip.x), my(tip.y));
      ctx.stroke();
    }
    ctx.lineCap = "butt";
    for (const ball of world.balls) {
      if (!ball.active) continue;
      ctx.fillStyle = ball.held ? "#ffd24d" : "#ffffff"; // held (seated / in scoop) balls drawn amber
      ctx.beginPath();
      ctx.arc(mx(ball.p.x), my(ball.p.y), Math.max(2.5, (ball.r / TABLE.W) * mapW), 0, 7);
      ctx.fill();
      if (ball.layer !== 1) {
        // ring in the layer colour when the ball is on a ramp / raised level
        ctx.strokeStyle = LAYER_COLORS[ball.layer] || "#fff";
        ctx.beginPath();
        ctx.arc(mx(ball.p.x), my(ball.p.y), Math.max(4.5, (ball.r / TABLE.W) * mapW + 2), 0, 7);
        ctx.stroke();
      }
    }
    ctx.fillStyle = "#7fd0ff";
    ctx.font = "10px monospace";
    ctx.fillText("PHYSICS DEBUG (D)", mapX, mapY - 9);
  }
}
